import { useState, useMemo } from "react";
import { useAppContext } from "@/lib/onboardingContext";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Hotel, Calculator, Clock, Utensils, BarChart3, FileText, Trophy,
  TrendingUp, TrendingDown, AlertTriangle, Check, ArrowUpRight, ArrowDownRight,
  DollarSign, Users, Building2, Sparkles, Target, Minus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, Cell,
} from "recharts";
import SectionEmptyState from "@/components/SectionEmptyState";

// ─── Utility ────────────────────────────────────────────────────────
const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${n.toFixed(1)}%`;
const delta = (current: number, prior: number) => prior === 0 ? 0 : ((current - prior) / prior) * 100;

function VarianceBadge({ value }: { value: number }) {
  if (Math.abs(value) < 0.5) return <Badge variant="secondary" className="text-xs gap-1"><Minus className="w-3 h-3" />0%</Badge>;
  return value > 0 ? (
    <Badge className="text-xs gap-1 bg-primary/10 text-primary border-primary/20">
      <ArrowUpRight className="w-3 h-3" />+{pct(value)}
    </Badge>
  ) : (
    <Badge variant="destructive" className="text-xs gap-1">
      <ArrowDownRight className="w-3 h-3" />{pct(value)}
    </Badge>
  );
}

const HOTEL_TYPES = ["hotel", "bed-and-breakfast", "bnb", "airbnb", "hostel", "motel", "resort"];
function isHotelType(bt: string) { return HOTEL_TYPES.includes(bt?.toLowerCase()); }

// ─── 1. RevPAR Tracker ──────────────────────────────────────────────
export function RevPARTracker() {
  const { onboarding } = useAppContext();
  const [rooms, setRooms] = useState(150);
  const [occupiedRooms, setOccupiedRooms] = useState(112);
  const [roomRevenue, setRoomRevenue] = useState(28000);

  if (!isHotelType(onboarding?.businessType || "")) return null;

  const occupancy = rooms > 0 ? (occupiedRooms / rooms) * 100 : 0;
  const adr = occupiedRooms > 0 ? roomRevenue / occupiedRooms : 0;
  const revpar = rooms > 0 ? roomRevenue / rooms : 0;
  const trevpar = rooms > 0 ? (roomRevenue * 1.35) / rooms : 0; // total rev estimate

  const trendData = [
    { day: "Mon", revpar: revpar * 0.82, adr: adr * 0.9, occ: occupancy * 0.85 },
    { day: "Tue", revpar: revpar * 0.88, adr: adr * 0.92, occ: occupancy * 0.9 },
    { day: "Wed", revpar: revpar * 0.95, adr: adr * 0.96, occ: occupancy * 0.95 },
    { day: "Thu", revpar: revpar * 1.02, adr: adr * 1.0, occ: occupancy * 1.0 },
    { day: "Fri", revpar: revpar * 1.18, adr: adr * 1.12, occ: occupancy * 1.05 },
    { day: "Sat", revpar: revpar * 1.25, adr: adr * 1.18, occ: occupancy * 1.08 },
    { day: "Sun", revpar: revpar * 0.92, adr: adr * 0.95, occ: occupancy * 0.88 },
  ];

  return (
    <div className="space-y-4">
      {/* Input Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Total Rooms</label>
          <Input type="number" value={rooms} onChange={e => setRooms(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Rooms Sold</label>
          <Input type="number" value={occupiedRooms} onChange={e => setOccupiedRooms(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Room Revenue ($)</label>
          <Input type="number" value={roomRevenue} onChange={e => setRoomRevenue(+e.target.value)} />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "RevPAR", value: fmt(revpar), sub: "Revenue Per Available Room", icon: Hotel, color: "text-primary" },
          { label: "ADR", value: fmt(adr), sub: "Average Daily Rate", icon: DollarSign, color: "text-primary" },
          { label: "Occupancy", value: pct(occupancy), sub: `${occupiedRooms} / ${rooms} rooms`, icon: Building2, color: occupancy >= 75 ? "text-primary" : "text-accent" },
          { label: "TRevPAR", value: fmt(trevpar), sub: "Total Revenue Per Avail Room", icon: TrendingUp, color: "text-primary" },
        ].map((kpi, i) => (
          <Card key={i} className="border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">{kpi.label}</span>
              </div>
              <div className={`text-2xl font-medium ${kpi.color}`}>{kpi.value}</div>
              <p className="text-xs text-muted-foreground mt-0.5">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Weekly RevPAR & ADR Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${v.toFixed(0)}`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revpar" name="RevPAR" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="adr" name="ADR" stroke="hsl(var(--accent))" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 2. GOP Calculator ──────────────────────────────────────────────
export function GOPCalculator() {
  const { onboarding } = useAppContext();
  const showRooms = isHotelType(onboarding?.businessType || "");
  const [totalRevenue, setTotalRevenue] = useState(450000);
  const [roomsRev, setRoomsRev] = useState(280000);
  const [fbRev, setFbRev] = useState(120000);
  const [otherRev, setOtherRev] = useState(50000);
  const [payroll, setPayroll] = useState(135000);
  const [operatingExp, setOperatingExp] = useState(67500);
  const [utilities, setUtilities] = useState(22500);
  const [marketing, setMarketing] = useState(13500);

  const totalExpenses = payroll + operatingExp + utilities + marketing;
  const gop = totalRevenue - totalExpenses;
  const gopMargin = totalRevenue > 0 ? (gop / totalRevenue) * 100 : 0;
  const flowThrough = 45; // target

  const expenseBreakdown = [
    { name: "Payroll", value: payroll, pct: totalRevenue > 0 ? (payroll / totalRevenue) * 100 : 0, benchmark: 30 },
    { name: "Operating", value: operatingExp, pct: totalRevenue > 0 ? (operatingExp / totalRevenue) * 100 : 0, benchmark: 15 },
    { name: "Utilities", value: utilities, pct: totalRevenue > 0 ? (utilities / totalRevenue) * 100 : 0, benchmark: 5 },
    { name: "Marketing", value: marketing, pct: totalRevenue > 0 ? (marketing / totalRevenue) * 100 : 0, benchmark: 3 },
  ];

  return (
    <div className="space-y-4">
      {/* Revenue Inputs */}
      <div className={`grid grid-cols-2 ${showRooms ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3`}>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Total Revenue</label>
          <Input type="number" value={totalRevenue} onChange={e => setTotalRevenue(+e.target.value)} />
        </div>
        {showRooms && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">Rooms Revenue</label>
            <Input type="number" value={roomsRev} onChange={e => setRoomsRev(+e.target.value)} />
          </div>
        )}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">F&B Revenue</label>
          <Input type="number" value={fbRev} onChange={e => setFbRev(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Other Revenue</label>
          <Input type="number" value={otherRev} onChange={e => setOtherRev(+e.target.value)} />
        </div>
      </div>

      {/* Expense Inputs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Payroll & Benefits</label>
          <Input type="number" value={payroll} onChange={e => setPayroll(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Operating Expenses</label>
          <Input type="number" value={operatingExp} onChange={e => setOperatingExp(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Utilities</label>
          <Input type="number" value={utilities} onChange={e => setUtilities(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Marketing</label>
          <Input type="number" value={marketing} onChange={e => setMarketing(+e.target.value)} />
        </div>
      </div>

      {/* GOP Result Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={`border-2 ${gopMargin >= 35 ? "border-primary/30 bg-primary/5" : gopMargin >= 25 ? "border-accent/30 bg-accent/5" : "border-destructive/30 bg-destructive/5"}`}>
          <CardContent className="p-4 text-center">
            <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">GOP</span>
            <div className={`text-3xl font-medium mt-1 ${gopMargin >= 35 ? "text-primary" : gopMargin >= 25 ? "text-accent-foreground" : "text-destructive"}`}>
              {fmt(gop)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Gross Operating Profit</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">GOP Margin</span>
            <div className={`text-3xl font-medium mt-1 ${gopMargin >= 35 ? "text-primary" : "text-accent-foreground"}`}>
              {pct(gopMargin)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Target: ≥35%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">Flow-Through</span>
            <div className="text-3xl font-medium mt-1 text-foreground">{flowThrough}%</div>
            <p className="text-xs text-muted-foreground mt-1">Target: ≥50%</p>
          </CardContent>
        </Card>
      </div>

      {/* Expense Breakdown Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Expense Ratio vs Benchmarks</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">% of Rev</TableHead>
                <TableHead className="text-right">Benchmark</TableHead>
                <TableHead className="text-right">Variance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenseBreakdown.map(row => (
                <TableRow key={row.name}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-right">{fmt(row.value)}</TableCell>
                  <TableCell className="text-right">{pct(row.pct)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{pct(row.benchmark)}</TableCell>
                  <TableCell className="text-right">
                    <VarianceBadge value={-(row.pct - row.benchmark)} />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium border-t-2">
                <TableCell>Total Expenses</TableCell>
                <TableCell className="text-right">{fmt(totalExpenses)}</TableCell>
                <TableCell className="text-right">{totalRevenue > 0 ? pct((totalExpenses / totalRevenue) * 100) : "–"}</TableCell>
                <TableCell className="text-right text-muted-foreground">53%</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 3. Labor Productivity Index ─────────────────────────────────────
export function LaborProductivityIndex() {
  const [revenue, setRevenue] = useState(450000);
  const [laborHours, setLaborHours] = useState(8400);
  const [laborCost, setLaborCost] = useState(135000);
  const [fte, setFte] = useState(52);

  const revPerLaborHr = laborHours > 0 ? revenue / laborHours : 0;
  const laborCostPct = revenue > 0 ? (laborCost / revenue) * 100 : 0;
  const revPerFte = fte > 0 ? revenue / fte : 0;
  const laborCostPerHr = laborHours > 0 ? laborCost / laborHours : 0;

  const deptData = [
    { dept: "Rooms", revPerHr: revPerLaborHr * 1.3, benchmark: 62, hours: Math.round(laborHours * 0.3) },
    { dept: "F&B", revPerHr: revPerLaborHr * 0.75, benchmark: 38, hours: Math.round(laborHours * 0.4) },
    { dept: "Spa", revPerHr: revPerLaborHr * 0.9, benchmark: 48, hours: Math.round(laborHours * 0.1) },
    { dept: "Admin", revPerHr: revPerLaborHr * 0.6, benchmark: 32, hours: Math.round(laborHours * 0.12) },
    { dept: "Maint", revPerHr: revPerLaborHr * 0.4, benchmark: 22, hours: Math.round(laborHours * 0.08) },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Monthly Revenue</label>
          <Input type="number" value={revenue} onChange={e => setRevenue(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Total Labor Hours</label>
          <Input type="number" value={laborHours} onChange={e => setLaborHours(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Total Labor Cost</label>
          <Input type="number" value={laborCost} onChange={e => setLaborCost(+e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Full-Time Equivalents</label>
          <Input type="number" value={fte} onChange={e => setFte(+e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Rev / Labor Hr", value: fmt(revPerLaborHr), benchmark: "$53.57", good: revPerLaborHr >= 53 },
          { label: "Labor Cost %", value: pct(laborCostPct), benchmark: "≤30%", good: laborCostPct <= 30 },
          { label: "Rev / FTE", value: fmt(revPerFte), benchmark: "$8,654", good: revPerFte >= 8000 },
          { label: "Cost / Hr", value: fmt(laborCostPerHr), benchmark: "$16.07", good: laborCostPerHr <= 18 },
        ].map((kpi, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">{kpi.label}</span>
              <div className={`text-2xl font-medium mt-1 ${kpi.good ? "text-primary" : "text-destructive"}`}>{kpi.value}</div>
              <p className="text-xs text-muted-foreground mt-0.5">Benchmark: {kpi.benchmark}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Revenue per Labor Hour by Department</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="dept" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revPerHr" name="Actual $/hr" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="benchmark" name="Benchmark" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} opacity={0.3} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 4. F&B Cost Variance Report ─────────────────────────────────────
export function FBCostVarianceReport() {
  const categories = [
    { category: "Food - Proteins", actual: 42000, budget: 38000, priorYear: 36500 },
    { category: "Food - Produce", actual: 12500, budget: 11000, priorYear: 10800 },
    { category: "Food - Dairy", actual: 6800, budget: 7000, priorYear: 6500 },
    { category: "Food - Dry Goods", actual: 8200, budget: 8500, priorYear: 7900 },
    { category: "Beverage - Liquor", actual: 15000, budget: 14500, priorYear: 13800 },
    { category: "Beverage - Wine", actual: 9500, budget: 10000, priorYear: 9200 },
    { category: "Beverage - Beer", actual: 5200, budget: 5000, priorYear: 4800 },
    { category: "Beverage - NA", actual: 2800, budget: 2500, priorYear: 2400 },
  ];

  const totals = categories.reduce(
    (acc, c) => ({ actual: acc.actual + c.actual, budget: acc.budget + c.budget, priorYear: acc.priorYear + c.priorYear }),
    { actual: 0, budget: 0, priorYear: 0 }
  );

  const fbRevenue = 320000;

  const chartData = categories.map(c => ({
    name: c.category.split(" - ")[1] || c.category,
    Actual: c.actual,
    Budget: c.budget,
    "Prior Year": c.priorYear,
  }));

  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={`border-2 ${totals.actual <= totals.budget ? "border-primary/30 bg-primary/5" : "border-destructive/30 bg-destructive/5"}`}>
          <CardContent className="p-4 text-center">
            <span className="text-xs font-medium uppercase text-muted-foreground">Total F&B Cost</span>
            <div className="text-2xl font-medium mt-1 text-foreground">{fmt(totals.actual)}</div>
            <p className="text-xs text-muted-foreground">{fbRevenue > 0 ? pct((totals.actual / fbRevenue) * 100) : "–"} of F&B Rev</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs font-medium uppercase text-muted-foreground">Budget Variance</span>
            <div className={`text-2xl font-medium mt-1 ${totals.actual <= totals.budget ? "text-primary" : "text-destructive"}`}>
              {totals.actual <= totals.budget ? "-" : "+"}{fmt(Math.abs(totals.actual - totals.budget))}
            </div>
            <VarianceBadge value={-delta(totals.actual, totals.budget)} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs font-medium uppercase text-muted-foreground">YoY Change</span>
            <div className="text-2xl font-medium mt-1 text-foreground">
              {fmt(totals.actual - totals.priorYear)}
            </div>
            <VarianceBadge value={-delta(totals.actual, totals.priorYear)} />
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Cost Comparison: Actual vs Budget vs Prior Year</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Actual" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Budget" fill="hsl(var(--accent))" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Prior Year" fill="hsl(var(--muted-foreground))" opacity={0.4} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detail Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Var $</TableHead>
                <TableHead className="text-right">Prior Yr</TableHead>
                <TableHead className="text-right">YoY %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map(c => (
                <TableRow key={c.category}>
                  <TableCell className="font-medium">{c.category}</TableCell>
                  <TableCell className="text-right">{fmt(c.actual)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmt(c.budget)}</TableCell>
                  <TableCell className="text-right">
                    <span className={c.actual <= c.budget ? "text-primary" : "text-destructive"}>
                      {c.actual <= c.budget ? "-" : "+"}{fmt(Math.abs(c.actual - c.budget))}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmt(c.priorYear)}</TableCell>
                  <TableCell className="text-right"><VarianceBadge value={-delta(c.actual, c.priorYear)} /></TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium border-t-2">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{fmt(totals.actual)}</TableCell>
                <TableCell className="text-right">{fmt(totals.budget)}</TableCell>
                <TableCell className="text-right">
                  <span className={totals.actual <= totals.budget ? "text-primary" : "text-destructive"}>
                    {totals.actual <= totals.budget ? "-" : "+"}{fmt(Math.abs(totals.actual - totals.budget))}
                  </span>
                </TableCell>
                <TableCell className="text-right">{fmt(totals.priorYear)}</TableCell>
                <TableCell className="text-right"><VarianceBadge value={-delta(totals.actual, totals.priorYear)} /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 5. Department P&L Breakdown ─────────────────────────────────────
export function DepartmentPLBreakdown() {
  const departments = [
    { name: "Rooms", revenue: 280000, directCost: 56000, payroll: 42000, other: 14000, color: "hsl(var(--primary))" },
    { name: "Food & Beverage", revenue: 120000, directCost: 42000, payroll: 48000, other: 12000, color: "hsl(var(--accent))" },
    { name: "Spa & Wellness", revenue: 35000, directCost: 8750, payroll: 14000, other: 3500, color: "hsl(162, 63%, 55%)" },
    { name: "Parking & Other", revenue: 15000, directCost: 3000, payroll: 4500, other: 1500, color: "hsl(220, 60%, 55%)" },
  ];

  const totals = departments.reduce(
    (acc, d) => ({
      revenue: acc.revenue + d.revenue,
      directCost: acc.directCost + d.directCost,
      payroll: acc.payroll + d.payroll,
      other: acc.other + d.other,
    }),
    { revenue: 0, directCost: 0, payroll: 0, other: 0 }
  );

  const chartData = departments.map(d => ({
    name: d.name,
    Revenue: d.revenue,
    "Direct Cost": d.directCost,
    Payroll: d.payroll,
    "Dept Profit": d.revenue - d.directCost - d.payroll - d.other,
  }));

  return (
    <div className="space-y-4">
      {/* Contribution Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Department Revenue & Profit Contribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Revenue" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Dept Profit" fill="hsl(var(--accent))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed P&L Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Direct Cost</TableHead>
                <TableHead className="text-right">Payroll</TableHead>
                <TableHead className="text-right">Other</TableHead>
                <TableHead className="text-right">Dept Profit</TableHead>
                <TableHead className="text-right">Margin %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {departments.map(d => {
                const profit = d.revenue - d.directCost - d.payroll - d.other;
                const margin = d.revenue > 0 ? (profit / d.revenue) * 100 : 0;
                return (
                  <TableRow key={d.name}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                      {d.name}
                    </TableCell>
                    <TableCell className="text-right">{fmt(d.revenue)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(d.directCost)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(d.payroll)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(d.other)}</TableCell>
                    <TableCell className={`text-right font-medium ${profit >= 0 ? "text-primary" : "text-destructive"}`}>{fmt(profit)}</TableCell>
                    <TableCell className={`text-right ${margin >= 40 ? "text-primary" : margin >= 20 ? "text-foreground" : "text-destructive"}`}>
                      {pct(margin)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="font-medium border-t-2">
                <TableCell>Total Property</TableCell>
                <TableCell className="text-right">{fmt(totals.revenue)}</TableCell>
                <TableCell className="text-right">{fmt(totals.directCost)}</TableCell>
                <TableCell className="text-right">{fmt(totals.payroll)}</TableCell>
                <TableCell className="text-right">{fmt(totals.other)}</TableCell>
                <TableCell className="text-right text-primary font-medium">
                  {fmt(totals.revenue - totals.directCost - totals.payroll - totals.other)}
                </TableCell>
                <TableCell className="text-right">
                  {pct(((totals.revenue - totals.directCost - totals.payroll - totals.other) / totals.revenue) * 100)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 6. Manager Daily Report Summary ─────────────────────────────────
export function ManagerDailyReport() {
  const { onboarding } = useAppContext();
  const showRooms = isHotelType(onboarding?.businessType || "");
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  // Editable state for all daily metrics
  const [roomsSold, setRoomsSold] = useState<string>("");
  const [totalRooms, setTotalRooms] = useState<string>("");
  const [adr, setAdr] = useState<string>("");
  const [revpar, setRevpar] = useState<string>("");
  const [fbCovers, setFbCovers] = useState<string>("");
  const [fbRevenue, setFbRevenue] = useState<string>("");
  const [fbCheck, setFbCheck] = useState<string>("");
  const [laborHours, setLaborHours] = useState<string>("");
  const [laborCost, setLaborCost] = useState<string>("");
  const [overtime, setOvertime] = useState<string>("");
  const [complaints, setComplaints] = useState<string>("");
  const [compliments, setCompliments] = useState<string>("");
  const [reviewScore, setReviewScore] = useState<string>("");
  const [cashDeposit, setCashDeposit] = useState<string>("");
  const [arBalance, setArBalance] = useState<string>("");

  const num = (v: string) => parseFloat(v) || 0;
  const hasData = num(fbCovers) > 0 || num(fbRevenue) > 0 || num(laborHours) > 0 || num(cashDeposit) > 0;

  // Generate action items from real data
  const actionItems: string[] = [];
  if (hasData) {
    if (num(overtime) > 10) actionItems.push(`Overtime at ${num(overtime)} hrs — review scheduling for next week`);
    if (num(complaints) > 0) actionItems.push(`${num(complaints)} guest complaint(s) — follow up required`);
    if (num(complaints) === 0 && num(compliments) > 0) actionItems.push(`Zero complaints today with ${num(compliments)} compliments — recognize the team`);
    if (num(arBalance) > 30000) actionItems.push(`AR balance at ${fmt(num(arBalance))} — schedule collection calls`);
    if (num(reviewScore) > 0 && num(reviewScore) < 4.0) actionItems.push(`Online review score at ${num(reviewScore).toFixed(1)} — investigate negative feedback`);
    if (num(fbRevenue) > 0 && num(fbCovers) > 0) {
      const avgCheck = num(fbRevenue) / num(fbCovers);
      if (avgCheck < 30) actionItems.push(`Average check is ${fmt(avgCheck)} — consider upselling strategies`);
    }
    if (num(laborCost) > 0 && num(fbRevenue) > 0) {
      const laborPct = (num(laborCost) / num(fbRevenue)) * 100;
      if (laborPct > 35) actionItems.push(`Labor cost is ${pct(laborPct)} of revenue — above the 30-35% target`);
    }
    if (showRooms && num(totalRooms) > 0 && num(roomsSold) > 0) {
      const occ = (num(roomsSold) / num(totalRooms)) * 100;
      if (occ < 60) actionItems.push(`Occupancy at ${pct(occ)} — consider rate adjustments or promotions`);
    }
    if (actionItems.length === 0) actionItems.push("All metrics look healthy — keep up the good work");
  }

  const InputField = ({ label, value, onChange, prefix }: { label: string; value: string; onChange: (v: string) => void; prefix?: string }) => (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="relative w-28">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{prefix}</span>}
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value}
          onChange={e => {
            const v = e.target.value.replace(/[^0-9.]/g, "");
            onChange(v);
          }}
          placeholder="—"
          className={`h-8 text-sm text-right font-medium [appearance:textfield] ${prefix ? "pl-5" : ""}`}
        />
      </div>
    </div>
  );

  const sections = [
    showRooms ? {
      title: "Rooms Performance",
      icon: Hotel,
      fields: [
        <InputField key="rs" label="Rooms Sold" value={roomsSold} onChange={setRoomsSold} />,
        <InputField key="tr" label="Total Rooms" value={totalRooms} onChange={setTotalRooms} />,
        <InputField key="adr" label="ADR" value={adr} onChange={setAdr} prefix="$" />,
        <InputField key="rp" label="RevPAR" value={revpar} onChange={setRevpar} prefix="$" />,
      ],
    } : null,
    {
      title: "Food & Beverage",
      icon: Utensils,
      fields: [
        <InputField key="cov" label="Covers" value={fbCovers} onChange={setFbCovers} />,
        <InputField key="rev" label="F&B Revenue" value={fbRevenue} onChange={setFbRevenue} prefix="$" />,
        <InputField key="chk" label="Average Check" value={fbCheck} onChange={setFbCheck} prefix="$" />,
      ],
    },
    {
      title: "Labor",
      icon: Users,
      fields: [
        <InputField key="hrs" label="Hours Worked" value={laborHours} onChange={setLaborHours} />,
        <InputField key="lc" label="Labor Cost" value={laborCost} onChange={setLaborCost} prefix="$" />,
        <InputField key="ot" label="Overtime Hours" value={overtime} onChange={setOvertime} />,
      ],
    },
    {
      title: "Guest Satisfaction",
      icon: Sparkles,
      fields: [
        <InputField key="comp" label="Complaints" value={complaints} onChange={setComplaints} />,
        <InputField key="cmpl" label="Compliments" value={compliments} onChange={setCompliments} />,
        <InputField key="rev" label="Online Review Avg" value={reviewScore} onChange={setReviewScore} />,
      ],
    },
    {
      title: "Cash & AR",
      icon: DollarSign,
      fields: [
        <InputField key="cd" label="Cash Deposit" value={cashDeposit} onChange={setCashDeposit} prefix="$" />,
        <InputField key="ar" label="AR Balance" value={arBalance} onChange={setArBalance} prefix="$" />,
      ],
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-medium text-foreground">Manager's Daily Report</h3>
          <p className="text-sm text-muted-foreground">{today}</p>
        </div>
        <Badge variant="outline" className="text-xs">{hasData ? "Data entered" : "Enter today's figures"}</Badge>
      </div>

      {/* Sections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.filter(Boolean).map(section => {
          const s = section!;
          const Icon = s.icon;
          return (
            <Card key={s.title}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">{s.title}</span>
                </div>
                <div className="space-y-2">
                  {s.fields}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Action Items — only shown when data is entered */}
      {hasData && (
        <Card className="border-accent/30 bg-accent/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-accent" />
              <span className="text-xs font-medium uppercase text-muted-foreground">Action Items</span>
            </div>
            <ul className="space-y-1.5 text-sm">
              {actionItems.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {!hasData && (
        <SectionEmptyState
          icon={FileText}
          title="No data entered yet"
          description="Enter today's figures above to generate AI-powered action items automatically."
          actionLabel="Start Logging"
          onAction={() => {
            const firstInput = document.querySelector<HTMLInputElement>('.space-y-4 input[type="text"]');
            firstInput?.focus();
          }}
        />
      )}
    </div>
  );
}

// ─── 7. KPI Benchmarking Tool ────────────────────────────────────────
export function KPIBenchmarkingTool() {
  const [selectedCity, setSelectedCity] = useState("Miami");
  const [conceptType, setConceptType] = useState("Full-Service Hotel");
  const [started, setStarted] = useState(false);

  const cities = ["Miami", "New York", "Los Angeles", "Chicago", "Dallas", "Atlanta", "San Francisco"];
  const concepts = ["Full-Service Hotel", "Select-Service", "Boutique Hotel", "Resort", "Extended Stay"];

  if (!started) {
    return (
      <SectionEmptyState
        icon={Trophy}
        title="No benchmarks configured"
        description="Compare your KPIs against industry averages for your market and concept type."
        actionLabel="Set Up Benchmarks"
        onAction={() => setStarted(true)}
      />
    );
  }

  // STR / NRA benchmark data by city
  const benchmarks: Record<string, Record<string, { revpar: number; adr: number; occ: number; laborPct: number; fbCostPct: number; gopMargin: number }>> = {
    "Miami": {
      "Full-Service Hotel": { revpar: 195, adr: 265, occ: 73.6, laborPct: 29.5, fbCostPct: 31.2, gopMargin: 38.4 },
      "Select-Service": { revpar: 145, adr: 185, occ: 78.4, laborPct: 24.8, fbCostPct: 28.5, gopMargin: 42.1 },
      "Boutique Hotel": { revpar: 220, adr: 310, occ: 71.0, laborPct: 31.2, fbCostPct: 33.5, gopMargin: 35.8 },
      "Resort": { revpar: 280, adr: 395, occ: 70.9, laborPct: 32.1, fbCostPct: 34.2, gopMargin: 36.5 },
      "Extended Stay": { revpar: 110, adr: 135, occ: 81.5, laborPct: 20.5, fbCostPct: 0, gopMargin: 48.2 },
    },
    "New York": {
      "Full-Service Hotel": { revpar: 245, adr: 325, occ: 75.4, laborPct: 33.2, fbCostPct: 32.8, gopMargin: 32.1 },
      "Select-Service": { revpar: 185, adr: 230, occ: 80.4, laborPct: 27.5, fbCostPct: 29.0, gopMargin: 38.5 },
      "Boutique Hotel": { revpar: 290, adr: 410, occ: 70.7, laborPct: 34.5, fbCostPct: 34.0, gopMargin: 30.2 },
      "Resort": { revpar: 220, adr: 350, occ: 62.9, laborPct: 35.0, fbCostPct: 35.0, gopMargin: 28.5 },
      "Extended Stay": { revpar: 155, adr: 185, occ: 83.8, laborPct: 22.0, fbCostPct: 0, gopMargin: 45.0 },
    },
    "Los Angeles": {
      "Full-Service Hotel": { revpar: 210, adr: 290, occ: 72.4, laborPct: 30.8, fbCostPct: 31.5, gopMargin: 36.2 },
      "Select-Service": { revpar: 155, adr: 200, occ: 77.5, laborPct: 26.0, fbCostPct: 28.8, gopMargin: 40.5 },
      "Boutique Hotel": { revpar: 250, adr: 350, occ: 71.4, laborPct: 32.0, fbCostPct: 33.0, gopMargin: 34.0 },
      "Resort": { revpar: 310, adr: 420, occ: 73.8, laborPct: 31.5, fbCostPct: 33.5, gopMargin: 37.0 },
      "Extended Stay": { revpar: 125, adr: 155, occ: 80.6, laborPct: 21.5, fbCostPct: 0, gopMargin: 46.5 },
    },
  };

  // Fallback for cities without data
  const getBenchmark = () => {
    const cityData = benchmarks[selectedCity];
    if (cityData && cityData[conceptType]) return cityData[conceptType];
    return benchmarks["Miami"]["Full-Service Hotel"];
  };

  const bm = getBenchmark();
  const yourMetrics = { revpar: 209, adr: 250, occ: 74.7, laborPct: 30.0, fbCostPct: 31.8, gopMargin: 36.2 };

  const radarData = [
    { metric: "RevPAR", you: Math.min((yourMetrics.revpar / bm.revpar) * 100, 150), benchmark: 100 },
    { metric: "ADR", you: Math.min((yourMetrics.adr / bm.adr) * 100, 150), benchmark: 100 },
    { metric: "Occupancy", you: Math.min((yourMetrics.occ / bm.occ) * 100, 150), benchmark: 100 },
    { metric: "GOP Margin", you: Math.min((yourMetrics.gopMargin / bm.gopMargin) * 100, 150), benchmark: 100 },
    { metric: "Labor Eff", you: Math.min((bm.laborPct / yourMetrics.laborPct) * 100, 150), benchmark: 100 },
    { metric: "F&B Cost", you: bm.fbCostPct > 0 ? Math.min((bm.fbCostPct / yourMetrics.fbCostPct) * 100, 150) : 100, benchmark: 100 },
  ];

  const comparisonRows = [
    { metric: "RevPAR", yours: fmt(yourMetrics.revpar), benchmark: fmt(bm.revpar), variance: delta(yourMetrics.revpar, bm.revpar) },
    { metric: "ADR", yours: fmt(yourMetrics.adr), benchmark: fmt(bm.adr), variance: delta(yourMetrics.adr, bm.adr) },
    { metric: "Occupancy %", yours: pct(yourMetrics.occ), benchmark: pct(bm.occ), variance: yourMetrics.occ - bm.occ },
    { metric: "GOP Margin %", yours: pct(yourMetrics.gopMargin), benchmark: pct(bm.gopMargin), variance: yourMetrics.gopMargin - bm.gopMargin },
    { metric: "Labor Cost %", yours: pct(yourMetrics.laborPct), benchmark: pct(bm.laborPct), variance: -(yourMetrics.laborPct - bm.laborPct) },
    { metric: "F&B Cost %", yours: pct(yourMetrics.fbCostPct), benchmark: bm.fbCostPct > 0 ? pct(bm.fbCostPct) : "N/A", variance: bm.fbCostPct > 0 ? -(yourMetrics.fbCostPct - bm.fbCostPct) : 0 },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Market / City</label>
          <select
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            value={selectedCity}
            onChange={e => setSelectedCity(e.target.value)}
          >
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Concept Type</label>
          <select
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            value={conceptType}
            onChange={e => setConceptType(e.target.value)}
          >
            {concepts.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Trophy className="w-3.5 h-3.5" />
        <span>Benchmarks based on STR Global & NRA industry reports for {selectedCity} — {conceptType}</span>
      </div>

      {/* Radar Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Your Performance vs Market Benchmark</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center">
          <div className="h-64 w-full max-w-md">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <PolarRadiusAxis domain={[0, 150]} tick={false} axisLine={false} />
                <Radar name="Your Property" dataKey="you" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.2} strokeWidth={2} />
                <Radar name="Market Benchmark" dataKey="benchmark" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted-foreground))" fillOpacity={0.05} strokeWidth={1.5} strokeDasharray="5 5" />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>KPI</TableHead>
                <TableHead className="text-right">Your Property</TableHead>
                <TableHead className="text-right">Market Avg</TableHead>
                <TableHead className="text-right">vs Benchmark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonRows.map(row => (
                <TableRow key={row.metric}>
                  <TableCell className="font-medium">{row.metric}</TableCell>
                  <TableCell className="text-right font-medium">{row.yours}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{row.benchmark}</TableCell>
                  <TableCell className="text-right"><VarianceBadge value={row.variance} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
